import React, { useState, useEffect } from "react";
import { getEmailSettings, updateEmailSettings, testEmailSettings, testImapSettings } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Mail, Server, Send, Save, Loader2, CheckCircle2, XCircle, Inbox } from "lucide-react";

const emptyAccount = { host: "", port: 465, user: "", password: "", from_email: "", admin_email: "", imap_host: "", imap_port: 993, has_password: false };

const ACCOUNTS = [
  {
    key: "notification",
    title: "Notifications (devis & messages)",
    desc: "Adresse expéditrice des emails transactionnels : accusés de réception de devis, alertes de nouveaux messages et confirmations client.",
    icon: Mail,
    showAdmin: true,
  },
  {
    key: "prospecting",
    title: "Prospection & campagnes",
    desc: "Adresse expéditrice utilisée par l'agent de prospection, les campagnes emailing et le pilote automatique.",
    icon: Send,
    showAdmin: false,
  },
];

const inputCls =
  "w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-brand";

const AccountCard = ({ meta, data, onChange, onTest, onTestImap, testing }) => {
  const Icon = meta.icon;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid={`email-account-${meta.key}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-brand/10 text-brand rounded-lg p-2.5 mt-0.5">
          <Icon size={18} />
        </div>
        <div>
          <h3 className="font-heading font-bold text-neutral-900">{meta.title}</h3>
          <p className="text-sm text-neutral-500 mt-0.5">{meta.desc}</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Adresse expéditrice (From)</label>
          <input
            className={inputCls}
            data-testid={`${meta.key}-from-input`}
            placeholder="prospect@ptitservices-pro.com"
            value={data.from_email}
            onChange={(e) => onChange(meta.key, "from_email", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Identifiant SMTP (login)</label>
          <input
            className={inputCls}
            data-testid={`${meta.key}-user-input`}
            placeholder="prospect@ptitservices-pro.com"
            value={data.user}
            onChange={(e) => onChange(meta.key, "user", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-600 mb-1">
            Mot de passe {data.has_password && <span className="text-emerald-600">· enregistré</span>}
          </label>
          <input
            type="password"
            className={inputCls}
            data-testid={`${meta.key}-password-input`}
            placeholder={data.has_password ? "•••••••• (laisser vide = inchangé)" : "mot de passe"}
            value={data.password}
            onChange={(e) => onChange(meta.key, "password", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Serveur SMTP (hôte)</label>
          <input
            className={inputCls}
            data-testid={`${meta.key}-host-input`}
            placeholder="peche.o2switch.net"
            value={data.host}
            onChange={(e) => onChange(meta.key, "host", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Port</label>
          <input
            type="number"
            className={inputCls}
            data-testid={`${meta.key}-port-input`}
            placeholder="465"
            value={data.port}
            onChange={(e) => onChange(meta.key, "port", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Serveur IMAP (réception)</label>
          <input
            className={inputCls}
            data-testid={`${meta.key}-imap-host-input`}
            placeholder="peche.o2switch.net"
            value={data.imap_host}
            onChange={(e) => onChange(meta.key, "imap_host", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-neutral-600 mb-1">Port IMAP</label>
          <input
            type="number"
            className={inputCls}
            data-testid={`${meta.key}-imap-port-input`}
            placeholder="993"
            value={data.imap_port}
            onChange={(e) => onChange(meta.key, "imap_port", e.target.value)}
          />
        </div>
        {meta.showAdmin && (
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-neutral-600 mb-1">
              Recevoir les notifications sur (email admin)
            </label>
            <input
              className={inputCls}
              data-testid={`${meta.key}-admin-input`}
              placeholder="contact@ptitservices-pro.com"
              value={data.admin_email}
              onChange={(e) => onChange(meta.key, "admin_email", e.target.value)}
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <span className="flex items-center gap-1.5 text-xs font-semibold">
          {data.host && data.user && (data.has_password || data.password) ? (
            <><CheckCircle2 size={14} className="text-emerald-600" /> <span className="text-emerald-600">Configuré</span></>
          ) : (
            <><XCircle size={14} className="text-amber-600" /> <span className="text-amber-600">Incomplet</span></>
          )}
        </span>
        <button
          onClick={() => onTestImap(meta.key)}
          disabled={testing === meta.key + "-imap"}
          data-testid={`${meta.key}-test-imap-btn`}
          className="ml-auto flex items-center gap-1.5 text-sm text-brand hover:underline disabled:opacity-60"
        >
          {testing === meta.key + "-imap" ? <Loader2 size={14} className="animate-spin" /> : <Inbox size={14} />}
          Tester la réception IMAP
        </button>
        <button
          onClick={() => onTest(meta.key)}
          disabled={testing === meta.key}
          data-testid={`${meta.key}-test-btn`}
          className="flex items-center gap-1.5 text-sm text-brand hover:underline disabled:opacity-60"
        >
          {testing === meta.key ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Envoyer un email de test
        </button>
      </div>
    </div>
  );
};

const EmailSettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    const res = await getEmailSettings();
    const data = res.data;
    setSettings({
      notification: { ...emptyAccount, ...data.notification, password: "" },
      prospecting: { ...emptyAccount, ...data.prospecting, password: "" },
    });
  };
   
  useEffect(() => { load(); }, []);

  const onChange = (account, field, value) => {
    setSettings((s) => ({ ...s, [account]: { ...s[account], [field]: value } }));
  };

  const buildPayload = () => ({
    notification: {
      host: settings.notification.host,
      port: parseInt(settings.notification.port, 10) || 465,
      user: settings.notification.user,
      password: settings.notification.password,
      from_email: settings.notification.from_email,
      admin_email: settings.notification.admin_email,
      imap_host: settings.notification.imap_host,
      imap_port: parseInt(settings.notification.imap_port, 10) || 993,
    },
    prospecting: {
      host: settings.prospecting.host,
      port: parseInt(settings.prospecting.port, 10) || 465,
      user: settings.prospecting.user,
      password: settings.prospecting.password,
      from_email: settings.prospecting.from_email,
      admin_email: "",
      imap_host: settings.prospecting.imap_host,
      imap_port: parseInt(settings.prospecting.imap_port, 10) || 993,
    },
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateEmailSettings(buildPayload());
      const data = res.data;
      setSettings({
        notification: { ...emptyAccount, ...data.notification, password: "" },
        prospecting: { ...emptyAccount, ...data.prospecting, password: "" },
      });
      toast({ title: "Paramètres enregistrés ✓" });
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec de l'enregistrement", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const test = async (purpose) => {
    const to = window.prompt("Adresse email destinataire du test :", settings[purpose].admin_email || settings[purpose].user || "");
    if (!to) return;
    setTesting(purpose);
    try {
      // Save first so the test uses the latest values.
      await updateEmailSettings(buildPayload());
      const res = await testEmailSettings({ purpose, to });
      if (res.data.ok) {
        toast({ title: "Email de test envoyé ✓", description: `Vérifiez la boîte ${to}.` });
      } else {
        toast({ title: "Échec de l'envoi", description: res.data.error || "Erreur SMTP", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec du test", variant: "destructive" });
    } finally {
      setTesting(null);
    }
  };

  const testImap = async (purpose) => {
    setTesting(purpose + "-imap");
    try {
      await updateEmailSettings(buildPayload());
      const res = await testImapSettings({ purpose, to: settings[purpose].user || "test@ptitservices-pro.com" });
      if (res.data.ok) {
        toast({ title: "Connexion IMAP réussie ✓", description: `${res.data.count} email(s) dans la boîte de réception.` });
      } else {
        toast({ title: "Échec IMAP", description: res.data.error || "Connexion impossible", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Échec du test IMAP", variant: "destructive" });
    } finally {
      setTesting(null);
    }
  };

  if (!settings) return <p className="text-neutral-500">Chargement…</p>;

  return (
    <div data-testid="email-settings-panel">
      <div className="flex items-center gap-2 mb-2">
        <Server size={20} className="text-brand" />
        <h2 className="font-heading font-bold text-xl text-neutral-900">Paramètres Email</h2>
      </div>
      <p className="text-sm text-neutral-500 mb-5">
        Gérez les adresses d'envoi, la configuration du serveur SMTP et la réception IMAP (boîte mail du CRM). Le mot de passe reste masqué et n'est
        remplacé que si vous en saisissez un nouveau.
      </p>

      <div className="space-y-5">
        {ACCOUNTS.map((meta) => (
          <AccountCard
            key={meta.key}
            meta={meta}
            data={settings[meta.key]}
            onChange={onChange}
            onTest={test}
            onTestImap={testImap}
            testing={testing}
          />
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="email-settings-save-btn"
        className="mt-5 flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-6 py-3 rounded-md disabled:opacity-60"
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        Enregistrer les paramètres
      </button>
    </div>
  );
};

export default EmailSettingsPanel;
